import { useCallAccess } from 'infrastructure/hooks/control-panel/use-call-access';
import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  DoNotCallRestrictionType,
  ClinicDoNotCallRestrictionType,
} from 'infrastructure/enums';
import { useInterval } from 'react-use';
import { doNotCallRestrictionTypeDataSource } from 'infrastructure/data-sources/do-not-call-restriction-types';

import s from './styles.module.scss';

import type { Nullable } from 'infrastructure/types';

const default_delay = 60_000 * 5;

interface IProps {
  doNotCallRestrictionType?: Nullable<DoNotCallRestrictionType>;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  clinicDoNotCallRestrictionType?: Nullable<ClinicDoNotCallRestrictionType>;
  clinicDoNotCallStartTime?: Nullable<string>;
  clinicDoNotCallEndTime?: Nullable<string>;
  patientTimezone?: Nullable<string>;
}

const Tag: React.FC<{ restrictionType: string }> = ({ restrictionType }) => {
  const { t } = useTranslation();

  const el = doNotCallRestrictionTypeDataSource({ type: 'clinic' }).findByField(
    'id',
    restrictionType,
  );

  return (
    <p className={classNames(s.tag, s.dnc)}>
      {t('labels.doNotCall')}: {el?.name}
    </p>
  );
};

const DncTag: React.FC<IProps> = ({
  doNotCallRestrictionType,
  doNotCallStartTime,
  doNotCallEndTime,
  clinicDoNotCallRestrictionType,
  clinicDoNotCallStartTime,
  clinicDoNotCallEndTime,
  patientTimezone,
}) => {
  const [, forceRecalc] = useState(0);

  const isPatientFull =
    doNotCallRestrictionType === DoNotCallRestrictionType.FullRestriction;
  const isPatientLimited =
    doNotCallRestrictionType === DoNotCallRestrictionType.LimitedHours;

  const isClinicFull =
    clinicDoNotCallRestrictionType ===
    ClinicDoNotCallRestrictionType.FullRestriction;
  const isClinicLimited =
    clinicDoNotCallRestrictionType ===
    ClinicDoNotCallRestrictionType.LimitedHours;

  const hasPatientWindow = !!(doNotCallStartTime && doNotCallEndTime);
  const hasClinicWindow = !!(
    clinicDoNotCallStartTime && clinicDoNotCallEndTime
  );

  const shouldTick =
    (isPatientLimited && hasPatientWindow) ||
    (isClinicLimited && hasClinicWindow);

  useInterval(() => forceRecalc(Date.now()), shouldTick ? default_delay : null);

  if (isPatientFull) {
    return <Tag restrictionType={doNotCallRestrictionType} />;
  }

  if (isPatientLimited && hasPatientWindow) {
    const { canCallRpm } = useCallAccess({
      doNotCallRestrictionType,
      doNotCallStartTime,
      doNotCallEndTime,
      patientTimezone,
    });

    return canCallRpm ? null : (
      <Tag restrictionType={doNotCallRestrictionType} />
    );
  }

  if (isClinicFull) {
    return <Tag restrictionType={clinicDoNotCallRestrictionType} />;
  }

  if (isClinicLimited && hasClinicWindow) {
    const { canCallRpm } = useCallAccess({
      doNotCallRestrictionType: clinicDoNotCallRestrictionType,
      doNotCallStartTime: clinicDoNotCallStartTime,
      doNotCallEndTime: clinicDoNotCallEndTime,
      patientTimezone,
    });

    return canCallRpm ? null : (
      <Tag restrictionType={clinicDoNotCallRestrictionType} />
    );
  }

  return null;
};

export default DncTag;
